import { spawnSync } from 'node:child_process';
import crypto, { randomBytes } from 'node:crypto';
import { readFileSync } from 'node:fs';

import type { ParsedCLIArgs } from '../cli/parseOptions.ts';

const NUMBER_OF_COMMITS_TO_FETCH = 50;
const FULL_SHA_REGEX = /^[a-f0-9]{40}$/i;

// Subset of the GitHub webhook event payload shapes written to GITHUB_EVENT_PATH.
// See https://docs.github.com/en/webhooks/webhook-events-and-payloads for full schemas.
interface GitHubEvent {
  pull_request?: {
    html_url: string;
    title: string | null;
    base: {
      sha: string;
    };
    head: {
      sha: string;
    };
  };
  head_commit?:
    | {
        url: string;
        message?: string;
      }
    | null;
  merge_group?: {
    head_sha: string;
    base_sha: string;
    head_commit?:
      | {
          message?: string;
        }
      | null;
  };
  repository?: {
    html_url: string;
    default_branch?: string;
  };
  ref?: string;
  before?: string;
  after?: string;
}

export interface EnvironmentResult {
  link: string | undefined;
  message: string | undefined;
  authorEmail: string | undefined;
  beforeSha: string;
  afterSha: string;
  nonce: string | undefined;
  debugMode: boolean;
  notify: string | undefined;
  fallbackShas: Array<string> | undefined;
  githubToken: string | undefined;
  ci: boolean;
  skip: string | undefined;
  only: string | undefined;
}

const envKeys: ReadonlyArray<string> = [
  'BUILD_REPOSITORY_URI',
  'BUILD_SOURCEVERSION',
  'CIRCLE_PROJECT_REPONAME',
  'CIRCLE_PROJECT_USERNAME',
  'CIRCLE_SHA1',
  'CI_PULL_REQUEST',
  'GITHUB_EVENT_PATH',
  'GITHUB_SERVER_URL',
  'GITHUB_SHA',
  'HAPPO_DEBUG',
  'SYSTEM_PULLREQUEST_PULLREQUESTID',
  'SYSTEM_PULLREQUEST_SOURCEBRANCH',
  'SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI',
  'SYSTEM_PULLREQUEST_TARGETBRANCH',
  'TRAVIS_COMMIT',
  'TRAVIS_COMMIT_RANGE',
  'TRAVIS_PULL_REQUEST',
  'TRAVIS_PULL_REQUEST_SHA',
  'TRAVIS_REPO_SLUG',
];

async function resolveGithubEvent(GITHUB_EVENT_PATH: string): Promise<GitHubEvent> {
  try {
    const fs = await import('node:fs/promises');
    const content = await fs.readFile(GITHUB_EVENT_PATH, 'utf8');
    return JSON.parse(content);
  } catch (e) {
    throw new Error(
      `Failed to load GitHub event from the GITHUB_EVENT_PATH environment variable: ${JSON.stringify(GITHUB_EVENT_PATH)}`,
      { cause: e },
    );
  }
}

async function resolveLink(
  cliArgs: ParsedCLIArgs,
  env: Record<string, string | undefined>,
): Promise<string | undefined> {
  if (cliArgs.link) {
    // Validate the link
    let parsed: URL;
    try {
      parsed = new URL(cliArgs.link);
    } catch (e) {
      throw new TypeError(
        `link must be a valid http/https URL. Invalid URL: '${cliArgs.link}'`,
        { cause: e },
      );
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new TypeError(
        `link must be a valid http/https URL. Invalid protocol: '${parsed.protocol}' (from '${cliArgs.link}')`,
      );
    }

    return cliArgs.link;
  }

  const {
    BUILD_REPOSITORY_URI,
    BUILD_SOURCEVERSION,

    // https://circleci.com/docs/reference/variables/
    CIRCLE_PROJECT_REPONAME,
    CIRCLE_PROJECT_USERNAME,
    CIRCLE_PULL_REQUEST,
    CIRCLE_SHA1,

    CI_PULL_REQUEST,

    // https://docs.github.com/en/actions/reference/workflows-and-actions/variables#default-environment-variables
    GITHUB_EVENT_PATH,
    GITHUB_SHA,

    SYSTEM_PULLREQUEST_PULLREQUESTID,
    SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI,

    // https://docs.travis-ci.com/user/environment-variables/#default-environment-variables
    TRAVIS_COMMIT,
    TRAVIS_PULL_REQUEST,
    TRAVIS_REPO_SLUG,
  } = env;

  if (CI_PULL_REQUEST) {
    // Circle CI
    return CI_PULL_REQUEST;
  }

  if (GITHUB_EVENT_PATH) {
    const ghEvent = await resolveGithubEvent(GITHUB_EVENT_PATH);

    if (ghEvent.pull_request) {
      return ghEvent.pull_request.html_url;
    }
    if (ghEvent.head_commit) {
      return ghEvent.head_commit.url;
    }
    if (ghEvent.merge_group && ghEvent.repository) {
      return `${ghEvent.repository.html_url}/commit/${ghEvent.merge_group.head_sha}`;
    }
    if (GITHUB_SHA && ghEvent.repository) {
      return `${ghEvent.repository.html_url}/commit/${GITHUB_SHA}`;
    }
  }

  if (SYSTEM_PULLREQUEST_PULLREQUESTID && SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI) {
    return `${SYSTEM_PULLREQUEST_SOURCEREPOSITORYURI}/pullrequest/${SYSTEM_PULLREQUEST_PULLREQUESTID}`.replace(
      /[^/]+@/,
      '',
    );
  }

  if (BUILD_REPOSITORY_URI && BUILD_SOURCEVERSION) {
    return `${BUILD_REPOSITORY_URI}/commit/${BUILD_SOURCEVERSION}`.replace(
      /[^/]+@/,
      '',
    );
  }

  const githubBase = 'https://github.com';

  if (TRAVIS_REPO_SLUG && TRAVIS_PULL_REQUEST) {
    return `${githubBase}/${TRAVIS_REPO_SLUG}/pull/${TRAVIS_PULL_REQUEST}`;
  }

  if (TRAVIS_REPO_SLUG && TRAVIS_COMMIT) {
    return `${githubBase}/${TRAVIS_REPO_SLUG}/commit/${TRAVIS_COMMIT}`;
  }

  if (CIRCLE_PULL_REQUEST) {
    return CIRCLE_PULL_REQUEST;
  }

  if (CIRCLE_PROJECT_USERNAME && CIRCLE_PROJECT_REPONAME && CIRCLE_SHA1) {
    return `${githubBase}/${CIRCLE_PROJECT_USERNAME}/${CIRCLE_PROJECT_REPONAME}/commit/${CIRCLE_SHA1}`;
  }

  return undefined;
}

async function resolveAuthorEmail(
  cliArgs: ParsedCLIArgs,
  env: Record<string, string | undefined>,
): Promise<string | undefined> {
  if (cliArgs.authorEmail) {
    return cliArgs.authorEmail;
  }

  const { GITHUB_EVENT_PATH } = env;

  if (GITHUB_EVENT_PATH) {
    // const ghEvent = await resolveGithubEvent(GITHUB_EVENT_PATH);
    // TODO: do something with the github event
  }

  const res = spawnSync('git', ['show', '-s', '--format=%ae'], {
    encoding: 'utf8',
  });

  if (res.status !== 0) {
    return undefined;
  }

  return res.stdout.trim();
}

function firstLine(message: string): string {
  const newlineIndex = message.indexOf('\n');
  return newlineIndex === -1 ? message : message.slice(0, newlineIndex);
}

async function resolveMessage(
  cliArgs: ParsedCLIArgs,
  env: Record<string, string | undefined>,
  afterSha: string,
): Promise<string | undefined> {
  if (cliArgs.message) {
    return cliArgs.message;
  }

  const { GITHUB_EVENT_PATH } = env;

  if (GITHUB_EVENT_PATH) {
    const ghEvent = await resolveGithubEvent(GITHUB_EVENT_PATH);
    const title = ghEvent.pull_request?.title;
    if (typeof title === 'string') {
      return title;
    }
    if (ghEvent.head_commit?.message) {
      return firstLine(ghEvent.head_commit.message);
    }
    if (ghEvent.merge_group?.head_commit?.message) {
      return firstLine(ghEvent.merge_group.head_commit.message);
    }
  }

  const res = spawnSync('git', ['log', '-1', '--pretty=%s', afterSha], {
    encoding: 'utf8',
  });

  if (res.status !== 0) {
    return undefined;
  }

  const message = res.stdout.split('\n')[0];

  if (!message) {
    return undefined;
  }

  return message;
}

function resolveShaFromTagMatcher(tagMatcher: string): string | undefined {
  const res = spawnSync(
    'git',
    ['tag', '--list', tagMatcher, '--sort', 'refname', '--no-contains'],
    {
      encoding: 'utf8',
    },
  );

  if (res.status !== 0) {
    throw new Error(
      `Failed to list git tags when matching against --beforeShaTagMatcher '${tagMatcher}'. Error: ${res.stderr}`,
    );
  }

  const rawAllTags = res.stdout.trim();
  if (!rawAllTags.length) {
    return undefined;
  }

  const allTags = rawAllTags.split('\n');
  const tag = allTags.at(-1);

  if (!tag) {
    throw new Error('No tag found matching the pattern');
  }

  const commitRes = spawnSync('git', ['rev-list', '-n', '1', tag], {
    encoding: 'utf8',
  });

  if (commitRes.status !== 0) {
    throw new Error(
      `Failed to resolve commit sha from tag "${tag}". Error: ${res.stderr}`,
    );
  }

  return commitRes.stdout.trim();
}

function fetchMoreHistory(
  ref: string, // can be a full SHA or a ref/branch name
  numberOfCommitsToFetch: number,
): boolean {
  const fetchRes = spawnSync(
    'git',
    ['fetch', 'origin', ref, `--depth=${numberOfCommitsToFetch}`],
    {
      encoding: 'utf8',
    },
  );
  const success = fetchRes.status === 0;
  if (!success) {
    console.error(
      `[HAPPO] Failed to fetch more history (${numberOfCommitsToFetch} commits) for ${ref}. Error: ${fetchRes.stderr}`,
    );
  }
  return success;
}

function resolveMergeBase(
  baseRef: string, // can be a full SHA or a ref/branch name
  afterSha: string,
  debugMode: boolean,
  tryNumber: number = 1,
): string | undefined {
  const res = spawnSync('git', ['merge-base', baseRef, afterSha], {
    encoding: 'utf8',
  });

  if (res.status !== 0) {
    // In GitHub Actions, checkouts can be shallow and may not include the PR base
    // commit (or enough of its history) to compute a merge-base.
    //
    // - First attempt often fails with "Not a valid commit name <sha>".
    // - After fetching just the base commit, a subsequent attempt can still fail
    //   with exit code 1 and empty stderr because history is still too shallow.
    //
    // When the base looks like a full SHA, we can try to fetch deeper history
    // and retry with increasing depth.
    if (FULL_SHA_REGEX.test(baseRef) && tryNumber <= 10) {
      if (debugMode) {
        console.error(
          `[HAPPO] When resolving the merge base between ${baseRef} and ${afterSha}, we failed to resolve the merge base. We'll try fetching more history (${tryNumber * NUMBER_OF_COMMITS_TO_FETCH} commits) and resolving the merge base again.`,
        );
      }
      if (fetchMoreHistory(baseRef, tryNumber * NUMBER_OF_COMMITS_TO_FETCH)) {
        return resolveMergeBase(baseRef, afterSha, debugMode, tryNumber + 1);
      }
    }
    console.error(
      `[HAPPO] Ignored error when resolving merge base between ${baseRef} and ${afterSha}: ${res.stderr}`,
    );
    return undefined;
  }

  const mergeBase = res.stdout.split('\n')[0]?.trim();

  if (!mergeBase) {
    console.error(
      `[HAPPO] git merge-base stdout is empty when resolving merge base between ${baseRef} and ${afterSha}. stdout: ${res.stdout}\nstderr: ${res.stderr}`,
    );
    return undefined;
  }

  return mergeBase;
}

async function resolveBeforeSha(
  cliArgs: ParsedCLIArgs,
  env: Record<string, string | undefined>,
  afterSha: string,
  debugMode: boolean,
): Promise<string | undefined> {
  if (cliArgs.beforeSha) {
    return cliArgs.beforeSha;
  }

  if (cliArgs.beforeShaTagMatcher) {
    const resolvedSha = resolveShaFromTagMatcher(cliArgs.beforeShaTagMatcher);
    if (resolvedSha) {
      return resolvedSha;
    }
  }

  const { TRAVIS_COMMIT_RANGE, GITHUB_EVENT_PATH, SYSTEM_PULLREQUEST_TARGETBRANCH } =
    env;

  if (GITHUB_EVENT_PATH) {
    const ghEvent = await resolveGithubEvent(GITHUB_EVENT_PATH);

    if (ghEvent.pull_request) {
      const resolvedSha = resolveMergeBase(
        ghEvent.pull_request.base.sha,
        afterSha,
        debugMode,
      );

      if (resolvedSha) {
        return resolvedSha;
      } else {
        console.error(
          `[HAPPO] Failed to resolve merge base commit for GitHub event ${ghEvent.pull_request.base.sha} and ${afterSha}. Falling back to pull request base SHA.`,
        );
        return ghEvent.pull_request.base.sha;
      }
    }

    if (ghEvent.merge_group) {
      return ghEvent.merge_group.base_sha;
    }

    // Don't create a comparison for pushes to the default branch (e.g. a merge commit landing on main).
    if (
      ghEvent.ref &&
      ghEvent.repository?.default_branch &&
      ghEvent.ref === `refs/heads/${ghEvent.repository.default_branch}`
    ) {
      return undefined;
    }

    return ghEvent.before;
  }

  if (TRAVIS_COMMIT_RANGE) {
    const [first] = TRAVIS_COMMIT_RANGE.split('...');
    return first;
  }

  let baseAzureBranch;
  if (SYSTEM_PULLREQUEST_TARGETBRANCH) {
    baseAzureBranch = [
      'origin',
      SYSTEM_PULLREQUEST_TARGETBRANCH.split('/').toReversed()[0],
    ].join('/');
  }

  const baseBranch = cliArgs.baseBranch || baseAzureBranch || 'origin/main';
  return resolveMergeBase(baseBranch, afterSha, debugMode);
}

function getHeadShaWithLocalChanges(): {
  headSha: string;
  headShaWithLocalChanges: string;
} {
  const randomSha = randomBytes(20).toString('hex');
  // Get the HEAD sha from the git repo, or if we have local changes, add them to the sha
  const res = spawnSync('git', ['rev-parse', 'HEAD'], {
    encoding: 'utf8',
  });
  if (res.status !== 0) {
    return { headSha: randomSha, headShaWithLocalChanges: randomSha };
  }
  const headSha = res.stdout.split('\n')[0];
  if (!headSha) {
    return { headSha: randomSha, headShaWithLocalChanges: randomSha };
  }

  // Check for local changes
  const diffRes = spawnSync('git', ['diff', 'HEAD'], {
    encoding: 'utf8',
  });

  // If git diff fails, return HEAD sha
  if (diffRes.status !== 0) {
    return { headSha, headShaWithLocalChanges: headSha };
  }

  const lsRes = spawnSync('git', ['ls-files', '--other', '--exclude-standard'], {
    encoding: 'utf8',
  });

  if (lsRes.status !== 0) {
    return { headSha, headShaWithLocalChanges: headSha };
  }

  const localChanges = [diffRes.stdout.trim(), lsRes.stdout.trim()];

  // Get contents of untracked files
  const untrackedFiles = lsRes.stdout
    .trim()
    .split('\n')
    .filter((file) => file.trim());

  for (const file of untrackedFiles) {
    try {
      const content = readFileSync(file, 'utf8');
      localChanges.push(content);
    } catch {
      // If we can't read the file, include just the filename
      localChanges.push(`${file}:<unreadable>`);
    }
  }

  const allChanges = localChanges.join('');

  if (!allChanges.trim()) {
    return { headSha, headShaWithLocalChanges: headSha };
  }

  // If there are local changes, create a hash that includes both HEAD and the changes
  const headShaWithLocalChanges = crypto
    .createHash('sha256')
    .update(headSha)
    .update(allChanges)
    .digest('hex')
    .slice(0, 40);

  return { headSha, headShaWithLocalChanges };
}

async function resolveAfterSha(
  cliArgs: ParsedCLIArgs,
  env: Record<string, string | undefined>,
): Promise<string | { headSha: string; headShaWithLocalChanges: string }> {
  if (cliArgs.afterSha) {
    return cliArgs.afterSha;
  }

  const {
    CIRCLE_SHA1,
    TRAVIS_PULL_REQUEST_SHA,
    TRAVIS_COMMIT,
    GITHUB_EVENT_PATH,
    GITHUB_SHA,
    BUILD_SOURCEVERSION,
    SYSTEM_PULLREQUEST_SOURCEBRANCH,
    CI,
  } = env;

  const sha = CIRCLE_SHA1 || TRAVIS_PULL_REQUEST_SHA || TRAVIS_COMMIT;

  if (sha) {
    return sha;
  }

  if (SYSTEM_PULLREQUEST_SOURCEBRANCH) {
    // azure pull request
    const rawBranchName = SYSTEM_PULLREQUEST_SOURCEBRANCH.split('/').toReversed()[0];
    const res = spawnSync('git', ['rev-parse', `origin/${rawBranchName}`], {
      encoding: 'utf8',
    });
    if (res.status === 0 && res.stdout) {
      const sha = res.stdout.split('\n')[0];
      if (sha) {
        return sha;
      }
    }
  }
  if (BUILD_SOURCEVERSION) {
    // azure master job
    return BUILD_SOURCEVERSION;
  }
  if (GITHUB_EVENT_PATH) {
    const ghEvent = await resolveGithubEvent(GITHUB_EVENT_PATH);
    if (ghEvent.pull_request) {
      return ghEvent.pull_request.head.sha;
    }
    if (ghEvent.merge_group) {
      return ghEvent.merge_group.head_sha;
    }
    if (ghEvent.after) {
      return ghEvent.after;
    }
    if (GITHUB_SHA) {
      return GITHUB_SHA;
    }
  }
  const headShaWithLocalChanges = getHeadShaWithLocalChanges();
  if (CI) {
    return headShaWithLocalChanges.headSha;
  }
  return headShaWithLocalChanges;
}

function resolveFallbackShas(
  cliArgs: ParsedCLIArgs,
  beforeSha: string | undefined,
): Array<string> | undefined {
  if (cliArgs.fallbackShas) {
    return cliArgs.fallbackShas.split(/[,\s]+/).filter(Boolean);
  }

  const fallbackShasCount = cliArgs.fallbackShasCount
    ? Number.parseInt(cliArgs.fallbackShasCount, 10)
    : 50;

  if (Number.isNaN(fallbackShasCount)) {
    throw new TypeError(
      `fallbackShasCount must be a number. Invalid value: '${cliArgs.fallbackShasCount}'`,
    );
  }

  const res = spawnSync(
    'git',
    [
      'log',
      '--format=%H',
      '--first-parent',
      `--max-count=${fallbackShasCount}`,
      `${beforeSha}^`,
    ],
    {
      encoding: 'utf8',
    },
  );

  if (res.status !== 0) {
    return undefined;
  }

  return res.stdout.split('\n').filter(Boolean);
}

function getRawEnv(
  env: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const res: Record<string, string | undefined> = {};
  for (const key of envKeys) {
    res[key] = env[key];
  }
  return res;
}

export default async function resolveEnvironment(
  cliArgs: ParsedCLIArgs,
  env: Record<string, string | undefined> = process.env,
): Promise<EnvironmentResult> {
  const debugMode = !!env.HAPPO_DEBUG;
  const afterSha = await resolveAfterSha(cliArgs, env);

  const realAfterSha = typeof afterSha === 'string' ? afterSha : afterSha.headSha;
  const afterShaWithLocalChanges =
    typeof afterSha === 'string' ? afterSha : afterSha.headShaWithLocalChanges;

  // Resolve the before SHA with the true HEAD SHA
  const [beforeSha, link, authorEmail, message] = await Promise.all([
    resolveBeforeSha(cliArgs, env, realAfterSha, debugMode),
    resolveLink(cliArgs, env),
    resolveAuthorEmail(cliArgs, env),

    // Resolve message with the SHA that includes local changes
    resolveMessage(cliArgs, env, afterShaWithLocalChanges),
  ]);

  const nonNullBeforeSha = beforeSha || afterShaWithLocalChanges;

  const result = {
    link,
    authorEmail,
    message,
    beforeSha: nonNullBeforeSha,
    afterSha: afterShaWithLocalChanges,
    nonce: cliArgs.nonce,
    debugMode,
    notify: cliArgs.notify,
    fallbackShas: resolveFallbackShas(cliArgs, nonNullBeforeSha),
    githubToken: cliArgs.githubToken,
    ci: !!env.CI,
    skip: cliArgs.skip,
    only: cliArgs.only,
  };

  if (debugMode) {
    console.log('[HAPPO] Raw environment', getRawEnv(env));
    console.log('[HAPPO] Resolved environment', result);
  }

  return result;
}
